// Starter marketing email templates + template category taxonomy.
//
// System templates are code-defined (no DB rows) and merged into the template
// gallery client-side, mirroring SYSTEM_NOTE_TEMPLATES. Applying one copies its
// subject/content into the campaign or journey email — same copy-on-insert
// semantics as org templates.
//
// Authoring constraints (all three must hold):
// 1. TipTap round-trip — content is edited in MarketingEmailEditor, which
//    normalizes HTML it doesn't model. Only use headings, paragraphs, lists,
//    blockquotes, hr, links, strong/em/u, and <span style="color:…"> —
//    table layouts and styled "button" links would be flattened on first edit.
// 2. API sanitizer (MarketingContentPolicyService) — allowlisted tags/attrs
//    only, no url() in inline CSS, https-only images.
// 3. Email clients — the sender wraps content in a 600px white card with
//    Arial 15px and appends the compliance/unsubscribe footer, so templates
//    are body fragments and never include their own footer or {{unsubscribeUrl}}.
//
// Personalization tokens resolved at send time: {{firstName}} (falls back to
// "there"), {{name}}, {{lastName}}, {{email}}, {{company}} (the CONTACT's
// company — not the sender's — and empty when unknown, so use it carefully).
// Anything else in {{…}} is sent as-is; sample copy therefore uses [brackets]
// for the bits the sender must replace.

import type { MarketingEmailDocument } from './marketing-email-blocks'
import { renderMarketingEmailDocument } from './marketing-email-blocks'

export const MARKETING_TEMPLATE_CATEGORIES = [
  { key: 'newsletter', label: 'Newsletter' },
  { key: 'announcement', label: 'Announcement' },
  { key: 'promotion', label: 'Promotion' },
  { key: 'event', label: 'Event' },
  { key: 'welcome', label: 'Welcome' },
  { key: 'engagement', label: 'Engagement' },
] as const

export type MarketingTemplateCategoryKey =
  (typeof MARKETING_TEMPLATE_CATEGORIES)[number]['key']

export function getMarketingTemplateCategoryLabel(
  key: string | null | undefined,
): string | null {
  if (!key) return null
  return (
    MARKETING_TEMPLATE_CATEGORIES.find(category => category.key === key)
      ?.label ?? null
  )
}

export const SYSTEM_MARKETING_TEMPLATE_PREFIX = 'system:'

export function isSystemMarketingTemplateId(id: string): boolean {
  return id.startsWith(SYSTEM_MARKETING_TEMPLATE_PREFIX)
}

export interface SystemMarketingTemplate {
  /** Stable id with the `system:` prefix — never stored as a campaign templateId */
  id: string
  name: string
  description: string
  category: MarketingTemplateCategoryKey
  subject: string
  contentHtml: string
  /**
   * Present on designer starters: applying the template opens it in block
   * ("Designer") mode. contentHtml is then always the serialized document.
   */
  document?: MarketingEmailDocument
}

const MUTED = 'color:#6b7280'

// ---------------------------------------------------------------------------
// Designer starters (block documents; contentHtml is derived)
// ---------------------------------------------------------------------------

const DESIGNED_LAUNCH_DOCUMENT: MarketingEmailDocument = {
  version: 1,
  blocks: [
    {
      id: 'l1',
      type: 'section',
      backgroundColor: '#111827',
      padding: 32,
      blocks: [
        {
          id: 'l2',
          type: 'heading',
          level: 1,
          align: 'center',
          text: 'Introducing [product]',
        },
        {
          id: 'l3',
          type: 'text',
          html: `<p style="text-align:center">The one-line pitch: what it does and who it's for.</p>`,
        },
        {
          id: 'l4',
          type: 'button',
          label: 'See it in action',
          url: 'https://example.com',
          align: 'center',
          backgroundColor: '#ffffff',
          textColor: '#111827',
        },
      ],
    },
    { id: 'l5', type: 'spacer', height: 8 },
    {
      id: 'l6',
      type: 'columns',
      left: [
        { id: 'l7', type: 'heading', level: 3, text: 'Why it matters' },
        {
          id: 'l8',
          type: 'text',
          html: '<p>Two or three sentences on the problem it solves — written from the reader’s point of view, not the roadmap’s.</p>',
        },
      ],
      right: [
        { id: 'l9', type: 'heading', level: 3, text: 'How to start' },
        {
          id: 'l10',
          type: 'text',
          html: '<p>The smallest first step that delivers a win today. One step, one link, no setup epic.</p>',
        },
      ],
    },
    { id: 'l11', type: 'spacer', height: 8 },
    {
      id: 'l12',
      type: 'text',
      html: `<p><span style="${MUTED}"><em>P.S. Available to everyone starting today.</em></span></p>`,
    },
  ],
}

const DESIGNED_SALE_DOCUMENT: MarketingEmailDocument = {
  version: 1,
  settings: { backgroundColor: '#fef2f2' },
  blocks: [
    {
      id: 's1',
      type: 'section',
      backgroundColor: '#dc2626',
      padding: 32,
      blocks: [
        {
          id: 's2',
          type: 'heading',
          level: 1,
          align: 'center',
          text: 'Take 20% off',
        },
        {
          id: 's3',
          type: 'text',
          html: `<p style="text-align:center">This week only — ends Sunday at midnight.</p><p style="text-align:center">Use code <strong>SAVE20</strong> at checkout.</p>`,
        },
        {
          id: 's4',
          type: 'button',
          label: 'Claim your 20% off',
          url: 'https://example.com',
          align: 'center',
          backgroundColor: '#ffffff',
          textColor: '#dc2626',
        },
      ],
    },
    {
      id: 's5',
      type: 'text',
      html: `<p>Hi {{firstName}}, one short paragraph on what the offer applies to and why now: a season, a launch, a milestone. Scarcity works when it's real — name the actual deadline.</p>`,
    },
    { id: 's7', type: 'divider' },
    {
      id: 's6',
      type: 'text',
      html: `<p><span style="${MUTED}"><em>Offer ends [date]. One use per customer; can't be combined with other discounts.</em></span></p>`,
    },
  ],
}

const DESIGNED_NEWSLETTER_DOCUMENT: MarketingEmailDocument = {
  version: 1,
  blocks: [
    {
      id: 'n1',
      type: 'section',
      backgroundColor: '#4f46e5',
      padding: 24,
      blocks: [
        {
          id: 'n1a',
          type: 'heading',
          level: 1,
          text: 'The monthly roundup',
        },
        {
          id: 'n1b',
          type: 'text',
          html: `<p>Everything worth knowing this month, in about three minutes.</p>`,
        },
      ],
    },
    {
      id: 'n2',
      type: 'text',
      html: `<p>Hi {{firstName}}, here's what's new.</p>`,
    },
    { id: 'n3', type: 'divider' },
    { id: 'n4', type: 'heading', level: 2, text: 'The headline story' },
    {
      id: 'n5',
      type: 'text',
      html: '<p>One or two sentences on the most important thing that happened. Lead with why it matters to the reader.</p>',
    },
    {
      id: 'n6',
      type: 'button',
      label: 'Read the full story',
      url: 'https://example.com',
      align: 'left',
    },
    { id: 'n7', type: 'divider' },
    {
      id: 'n8',
      type: 'columns',
      left: [
        { id: 'n9', type: 'heading', level: 3, text: 'Worth a look' },
        {
          id: 'n10',
          type: 'text',
          html: '<p>A second story or customer win — keep it to the hook. <a href="https://example.com">Take a look →</a></p>',
        },
      ],
      right: [
        { id: 'n11', type: 'heading', level: 3, text: 'One quick tip' },
        {
          id: 'n12',
          type: 'text',
          html: '<p>Something immediately useful the reader can apply today, even if they click nothing else.</p>',
        },
      ],
    },
    { id: 'n13', type: 'spacer', height: 8 },
    {
      id: 'n14',
      type: 'section',
      backgroundColor: '#f4f4f5',
      padding: 16,
      blocks: [
        {
          id: 'n15',
          type: 'text',
          html: `<p style="text-align:center"><span style="${MUTED}">Thanks for reading — see you next month.</span></p>`,
        },
      ],
    },
  ],
}

export const SYSTEM_MARKETING_TEMPLATES: SystemMarketingTemplate[] = [
  {
    id: 'system:designed-launch',
    name: 'Product launch — designed',
    description:
      'Block layout: centered hero, CTA button, and a two-column "why / how" section. Opens in the designer.',
    category: 'announcement',
    subject: 'Introducing [product] — here’s what it does for you',
    document: DESIGNED_LAUNCH_DOCUMENT,
    contentHtml: renderMarketingEmailDocument(DESIGNED_LAUNCH_DOCUMENT),
  },
  {
    id: 'system:designed-sale',
    name: 'Flash sale — designed',
    description:
      'Block layout: bold offer headline, discount code, and a high-contrast CTA button. Opens in the designer.',
    category: 'promotion',
    subject: '20% off — this week only',
    document: DESIGNED_SALE_DOCUMENT,
    contentHtml: renderMarketingEmailDocument(DESIGNED_SALE_DOCUMENT),
  },
  {
    id: 'system:designed-newsletter',
    name: 'Newsletter — designed',
    description:
      'Block layout: headline story with CTA, then a two-column secondary section. Opens in the designer.',
    category: 'newsletter',
    subject: 'The monthly roundup — 3 things worth your time',
    document: DESIGNED_NEWSLETTER_DOCUMENT,
    contentHtml: renderMarketingEmailDocument(DESIGNED_NEWSLETTER_DOCUMENT),
  },
  {
    id: 'system:newsletter-roundup',
    name: 'Monthly roundup',
    description:
      'A three-story newsletter with a scannable structure: headline story, secondary story, and a quick tip.',
    category: 'newsletter',
    subject: 'The monthly roundup — 3 things worth your time',
    contentHtml: `<h1>The monthly roundup</h1>
<p><span style="${MUTED}">Everything worth knowing this month, in about three minutes.</span></p>
<p>Hi {{firstName}},</p>
<p>Here's what's new — three quick reads before you get back to your day.</p>
<hr>
<h2>1. The headline story</h2>
<p>One or two sentences on the most important thing that happened. Lead with why it matters to the reader, not to you.</p>
<p><a href="https://example.com"><strong>Read the full story →</strong></a></p>
<h2>2. Worth a look</h2>
<p>A second update, customer story, or piece of content. Keep it to the hook — the link carries the detail.</p>
<p><a href="https://example.com"><strong>Take a look →</strong></a></p>
<h2>3. One quick tip</h2>
<p>End with something immediately useful the reader can apply today, even if they click nothing else.</p>
<hr>
<p><span style="${MUTED}">Thanks for reading — see you next month.</span><br><span style="${MUTED}">The [Company] team</span></p>`,
  },
  {
    id: 'system:plain-note',
    name: 'Plain personal note',
    description:
      'A short, text-only email that reads one-to-one. No headline, no images — the format with the best reply rates.',
    category: 'engagement',
    subject: 'A quick note',
    contentHtml: `<p>Hi {{firstName}},</p>
<p>Just a short, personal note — no big headline, no graphics. Emails like this feel like they were written for one person, and they consistently get the most replies.</p>
<p>Say the one thing you want the reader to know. Then ask the one question you'd like them to answer — and make it easy to answer by replying directly to this email.</p>
<p>Best,<br>[Your name]</p>`,
  },
  {
    id: 'system:product-announcement',
    name: 'Product announcement',
    description:
      'A launch email: centered headline, one-line pitch, benefit bullets, and a single call to action.',
    category: 'announcement',
    subject: 'Introducing [product] — here’s what it does for you',
    contentHtml: `<h1 style="text-align:center">Introducing [product]</h1>
<p style="text-align:center"><span style="${MUTED}">The one-line pitch: what it does and who it's for.</span></p>
<hr>
<p>Hi {{firstName}},</p>
<p>Today we're launching [product]. Two or three sentences on the problem it solves and why you built it — written from the reader's point of view.</p>
<h3>What you can do with it</h3>
<ul>
<li><strong>Benefit one</strong> — the outcome, not the feature.</li>
<li><strong>Benefit two</strong> — what gets faster, cheaper, or easier.</li>
<li><strong>Benefit three</strong> — the thing nobody else does.</li>
</ul>
<p style="text-align:center"><a href="https://example.com"><strong>See it in action →</strong></a></p>
<p><span style="${MUTED}"><em>P.S. Available to everyone starting today — no setup required.</em></span></p>`,
  },
  {
    id: 'system:whats-new',
    name: 'What’s new digest',
    description:
      'A changelog-style update: three shipped improvements, each with a short explanation and why it matters.',
    category: 'announcement',
    subject: 'What’s new: 3 improvements you asked for',
    contentHtml: `<h2>What's new this month</h2>
<p>Hi {{firstName}}, here's what shipped recently — including a few things you asked for.</p>
<h3>Improvement one</h3>
<p>What changed and what it does for you. One paragraph, plain language, no version numbers.</p>
<h3>Improvement two</h3>
<p>What changed, and the situation where you'll notice it.</p>
<h3>Improvement three</h3>
<p>The small quality-of-life fix people will quietly love.</p>
<hr>
<p><a href="https://example.com"><strong>See the full changelog →</strong></a></p>`,
  },
  {
    id: 'system:promo-offer',
    name: 'Limited-time offer',
    description:
      'A promotion with a bold centered offer, a discount code, one CTA, and a clear end date.',
    category: 'promotion',
    subject: '20% off — this week only',
    contentHtml: `<h1 style="text-align:center">Take 20% off</h1>
<p style="text-align:center"><span style="${MUTED}">This week only — the offer ends Sunday at midnight.</span></p>
<p style="text-align:center">Use code <strong>SAVE20</strong> at checkout.</p>
<hr>
<p>Hi {{firstName}},</p>
<p>One short paragraph on what the offer applies to and why now: a season, a launch, a milestone. Scarcity works when it's real — name the actual deadline.</p>
<p style="text-align:center"><a href="https://example.com"><strong>Claim your 20% off →</strong></a></p>
<p><span style="${MUTED}"><em>Offer ends [date]. One use per customer; can't be combined with other discounts.</em></span></p>`,
  },
  {
    id: 'system:event-invite',
    name: 'Event invitation',
    description:
      'An invite with the when/where details called out in a block, what to expect, and an RSVP link.',
    category: 'event',
    subject: 'You’re invited: [event name]',
    contentHtml: `<h1>You're invited</h1>
<p>Hi {{firstName}},</p>
<p>We'd love to see you at <strong>[event name]</strong> — one sentence on what it is and who it's for.</p>
<blockquote>
<p><strong>When:</strong> [day, date · start–end time, timezone]<br><strong>Where:</strong> [venue or link]<br><strong>Cost:</strong> Free — registration required</p>
</blockquote>
<h3>What to expect</h3>
<ul>
<li>The headline session or speaker.</li>
<li>Who you'll meet there.</li>
<li>The practical thing you'll leave with.</li>
</ul>
<p><a href="https://example.com"><strong>Save your seat →</strong></a></p>
<p><span style="${MUTED}">Can't make it? Reply and we'll send you the recap.</span></p>`,
  },
  {
    id: 'system:webinar-invite',
    name: 'Webinar invitation',
    description:
      'A live-session invite with an agenda, a speaker line, and a registration CTA.',
    category: 'event',
    subject: 'Join us live: [topic] on [date]',
    contentHtml: `<h1>Join us live: [topic]</h1>
<p><span style="${MUTED}">[Day, date] · [time + timezone] · 45 minutes, including Q&amp;A</span></p>
<p>Hi {{firstName}},</p>
<p>One paragraph on the problem this session tackles and the outcome attendees leave with. Registrants get the recording even if they can't attend live.</p>
<h3>On the agenda</h3>
<ol>
<li>The core idea in ten minutes.</li>
<li>A live walkthrough of a real example.</li>
<li>Your questions, answered live.</li>
</ol>
<blockquote>
<p><em>Hosted by [speaker name], [role] — one line on why they're worth 45 minutes.</em></p>
</blockquote>
<p><a href="https://example.com"><strong>Register free →</strong></a></p>`,
  },
  {
    id: 'system:welcome',
    name: 'Welcome email',
    description:
      'The first email after signup: a warm hello, three ways to get started, and a human reply-to invitation.',
    category: 'welcome',
    subject: 'Welcome — here’s how to get started',
    contentHtml: `<h1>Welcome, {{firstName}}!</h1>
<p>We're glad you're here. One sentence on the promise: what this product or newsletter will do for them.</p>
<h3>Three ways to get started</h3>
<ol>
<li><strong>Do the first thing</strong> — the smallest step that delivers a win today.</li>
<li><strong>Set up the second thing</strong> — the step that makes it stick.</li>
<li><strong>Explore the third thing</strong> — where to go once the basics feel easy.</li>
</ol>
<p><a href="https://example.com"><strong>Get started →</strong></a></p>
<p><span style="${MUTED}">Questions? Just reply to this email — a real person reads every message.</span></p>`,
  },
  {
    id: 'system:re-engagement',
    name: 'Win-back',
    description:
      'A re-engagement email for lapsed contacts: what changed since they left, one CTA, and a graceful out.',
    category: 'engagement',
    subject: 'It’s been a while — here’s what you missed',
    contentHtml: `<h2>It's been a while, {{firstName}}</h2>
<p>No guilt trip — just the honest highlights of what's changed since you last stopped by:</p>
<ul>
<li>The biggest improvement, in one line.</li>
<li>The new thing people ask about most.</li>
<li>The fix for the thing that used to be annoying.</li>
</ul>
<p><a href="https://example.com"><strong>Pick up where you left off →</strong></a></p>
<hr>
<p><span style="${MUTED}">Not interested anymore? No hard feelings — there's an unsubscribe link below and we won't take it personally.</span></p>`,
  },
  {
    id: 'system:feedback-request',
    name: 'Feedback request',
    description:
      'A short, honest ask for feedback with a single link and a reason the answer matters.',
    category: 'engagement',
    subject: 'Got 2 minutes? Tell us what you think',
    contentHtml: `<p>Hi {{firstName}},</p>
<p>A small favor: we're deciding what to build next, and your answer genuinely shapes the list. It's three questions and takes about two minutes.</p>
<p style="text-align:center"><a href="https://example.com"><strong>Share your thoughts →</strong></a></p>
<p>If you'd rather just tell us directly — reply to this email. We read everything.</p>
<p>Thank you!<br>[Your name]</p>`,
  },
]
